/**
 * Who gets asked to set a personal password, as a pure decision.
 *
 * The transition (spec 2026-09-22): brand magic links keep working, and the
 * portal asks once per visit for a personal account. Everything person-bound —
 * the WhatsApp channel today, the chat later — needs a person, and a magic link
 * knows a brand but nobody in particular.
 *
 * Fail-closed, and that direction is the whole point. A missing `personal`
 * (an older server), a brand that has not loaded, a storage that throws: all of
 * it reads as "do not nudge" or "not dismissed", never as a phantom prompt and
 * never as a swallowed one. The only input that produces `nudge` is a brand
 * that says `personal === false` out loud.
 *
 * Dismissal lives in `sessionStorage` on purpose: it lasts the visit, not the
 * device. Next visit asks again — Alan's choice, not an oversight.
 *
 * @module web/screens/app/passwordNudge
 */
import type { BrandResponse } from "../../api/types.ts";

/** What the shell should render: nothing, or the banner. */
export type PasswordNudgeState = "hidden" | "nudge";

/**
 * Whether this session sees the banner.
 *
 * Pure: the caller reads storage, this decides. `personal` is optional on
 * `BrandResponse` because an older server omits it — absent is treated as
 * `false` would NOT be safe here (it would nudge people who are already
 * personal), so the check is for the explicit `false`.
 */
export function passwordNudgeState(
  brand: Pick<BrandResponse, "personal"> | null | undefined,
  dismissed: boolean,
): PasswordNudgeState {
  if (dismissed) return "hidden";
  return brand?.personal === false ? "nudge" : "hidden";
}

/** The slice of Storage this needs. Injectable, so the tests are not a browser. */
export interface VisitStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const NUDGE_DISMISSED_KEY = "passwordNudgeDismissed";

/** The session store, or null where there is not one to be had. */
export function browserVisitStore(): VisitStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Was the banner waved away during this visit?
 *
 * A private window can throw on the property access or on the read itself. It
 * answers `false` there, so the banner shows: an extra ask is a small
 * annoyance, a thrown exception on boot is a white screen.
 */
export function readDismissed(store: VisitStore | null): boolean {
  if (!store) return false;
  try {
    return store.getItem(NUDGE_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remember the dismissal for this visit. Failing to remember is not an error. */
export function rememberDismissed(store: VisitStore | null): void {
  if (!store) return;
  try {
    store.setItem(NUDGE_DISMISSED_KEY, "1");
  } catch {
    // Asking again next render is worse than asking again next visit, but it is
    // not broken — and the component keeps its own state for this render anyway.
  }
}
